/**
 * ModulesPage — F12.A T12.A6.
 *
 * Lista modułów custom tenanta + tworzenie nowych.
 * Routing: `/modules` (tenant scope, gated by ModuleGuard 'crm_core' + permission `crm_core.custom_modules.view`).
 *
 * UX (adaptacja z SellCall, uproszczona dla MVP):
 *   - Grid kafelków z ikoną + kolorem + nazwą + opisem
 *   - Per kafelek: link do `/modules/:id/builder` + menu (Records / Usuń)
 *   - „+ Nowy moduł" → modal: name, description, color preset (8 kolorów), showInMenu
 *     Po create: auto-redirect do `/modules/:id/builder` (tenant od razu konfiguruje pola)
 *   - Empty state z dużym CTA
 *
 * NEG (deferred):
 *   - Icon picker (na MVP używamy default 'Database' icon, edycja icona w T12.A8)
 *   - System templates (zainstaluj szablon)
 *   - Dezaktywuj (toggle isActive) — w T12.A12 jeśli zostanie czas
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Database,
  MoreVertical,
  Pencil,
  Plus,
  Settings as SettingsIcon,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { EditModuleModal } from '@/components/modules/EditModuleModal';
import { IconPicker, renderIconByName } from '@/components/modules/IconPicker';
import { Spinner } from '@/components/Spinner';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { useReservedFieldSlugToast } from '@/hooks/useReservedFieldSlugToast';
import { api } from '@/lib/api';

interface ModuleListItem {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string;
  color: string;
  isActive: boolean;
  showInMenu: boolean;
  createdAt: string;
  // F12.D bug-fix 2026-05-11 — soft-delete + restore
  gcrecord?: string | null;
  /** Szablon huba, z którego kartotekę zainstalowało ITE; `null` = utworzona przez tenanta. */
  sourceTemplateModuleId?: string | null;
}

const PRESET_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#6B7280', // gray
];

interface NewModuleInput {
  name: string;
  description: string;
  icon: string;
  color: string;
  showInMenu: boolean;
}

const DEFAULT_NEW_MODULE: NewModuleInput = {
  name: '',
  description: '',
  icon: 'Database',
  color: '#3B82F6',
  showInMenu: true,
};

// ─── Page root ──────────────────────────────────────────────────────────────

export function ModulesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const showReservedSlugToast = useReservedFieldSlugToast();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newModule, setNewModule] = useState<NewModuleInput>(DEFAULT_NEW_MODULE);
  const [pendingDelete, setPendingDelete] = useState<ModuleListItem | null>(null);
  // F12.D bug-fix 2026-05-11 — toggle „Pokaż usunięte" + przywracanie modułów
  const [showDeleted, setShowDeleted] = useState(false);

  const modulesQuery = useQuery({
    queryKey: ['modules', { showDeleted }],
    queryFn: async () => {
      const params = showDeleted ? '?include_deleted=true' : '';
      const res = await api.get<{ modules: ModuleListItem[] }>(`/modules${params}`);
      return res.data.modules;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (input: NewModuleInput) => {
      const res = await api.post<{ module: ModuleListItem }>('/modules', input);
      return res.data.module;
    },
    onSuccess: (mod) => {
      toast.show({ variant: 'success', title: t('tenant.modules.toasts.created') });
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      queryClient.invalidateQueries({ queryKey: ['modules', 'menu'] });
      setShowCreateModal(false);
      setNewModule(DEFAULT_NEW_MODULE);
      // Auto-redirect do builder (tenant od razu konfiguruje pola)
      navigate(`/modules/${mod.id}/builder`);
    },
    onError: (err) => {
      const message = isAxiosError(err)
        ? (err.response?.data?.error ?? t('tenant.modules.toasts.createError'))
        : t('tenant.modules.toasts.createError');
      toast.show({
        variant: 'error',
        title: t('tenant.modules.toasts.createError'),
        description: String(message),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/modules/${id}`);
      return id;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.toasts.deleted') });
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      queryClient.invalidateQueries({ queryKey: ['modules', 'menu'] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.toasts.deleteError') });
    },
  });

  // F12.D bug-fix 2026-05-11 — przywracanie modułu z kosza
  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/modules/${id}/restore`);
      return id;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.toasts.restored') });
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      queryClient.invalidateQueries({ queryKey: ['modules', 'menu'] });
    },
    onError: (err) => {
      // 400 FIELD_SLUG_RESERVED: odtwarzana tabela ma pole o nazwie technicznej zarezerwowanej
      // przez system (np. „decisions") — komunikat wskazuje pole; kartoteka zostaje w koszu.
      // Komunikat zostaje do ręcznego zamknięcia — treść do przekazania opiekunowi (wspólny hak).
      const title = t('tenant.modules.toasts.restoreError');
      if (showReservedSlugToast(err, { title, variant: 'support' })) return;
      toast.show({ variant: 'error', title });
    },
  });

  const handleCreate = () => {
    if (!newModule.name.trim()) return;
    createMutation.mutate(newModule);
  };

  const handleDelete = (mod: ModuleListItem) => {
    setPendingDelete(mod);
  };

  return (
    <main className="min-h-full bg-bg-secondary p-6" data-testid="modules-page">
      <div className="space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">{t('tenant.modules.title')}</h1>
            <p className="mt-1 text-sm text-text-tertiary">{t('tenant.modules.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                data-testid="modules-show-deleted-toggle"
                className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
              />
              {t('tenant.modules.showDeleted')}
            </label>
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setShowCreateModal(true)}
              data-testid="modules-new-btn"
            >
              {t('tenant.modules.newModule')}
            </Button>
          </div>
        </header>

        {modulesQuery.isLoading && (
          <div className="flex justify-center py-12" data-testid="modules-loading">
            <Spinner />
          </div>
        )}

        {modulesQuery.isError && (
          <Card padding="md">
            <p className="text-error-500">{t('tenant.modules.loadError')}</p>
          </Card>
        )}

        {modulesQuery.data && modulesQuery.data.length === 0 && (
          <EmptyState onCreate={() => setShowCreateModal(true)} />
        )}

        {/* Wszystko, co zainstalowało ITE (szablon huba), jest dla tenanta SYSTEMOWE —
            „własne" to tylko kartoteki utworzone przez tenanta (ten sam podział co
            w ustawieniach: przegląd kartotek). */}
        {modulesQuery.data &&
          modulesQuery.data.length > 0 &&
          (
            [
              {
                key: 'system',
                titleKey: 'tenant.settings.modulesOverview.systemSection',
                items: modulesQuery.data.filter((m) => m.sourceTemplateModuleId != null),
              },
              {
                key: 'custom',
                titleKey: 'tenant.settings.modulesOverview.customSection',
                items: modulesQuery.data.filter((m) => m.sourceTemplateModuleId == null),
              },
            ] as const
          ).map(
            (group) =>
              group.items.length > 0 && (
                <section key={group.key} className="space-y-3" data-testid={`modules-${group.key}`}>
                  <h2 className="text-lg font-semibold text-text-primary">{t(group.titleKey)}</h2>
                  <div
                    className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
                    data-testid={group.key === 'custom' ? 'modules-grid' : 'modules-grid-system'}
                  >
                    {group.items.map((mod) => (
                      <ModuleCard
                        key={mod.id}
                        mod={mod}
                        onDelete={() => handleDelete(mod)}
                        onRestore={() => restoreMutation.mutate(mod.id)}
                        restoreLoading={
                          restoreMutation.isPending && restoreMutation.variables === mod.id
                        }
                      />
                    ))}
                  </div>
                </section>
              ),
          )}

        <Modal
          open={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setNewModule(DEFAULT_NEW_MODULE);
          }}
          title={t('tenant.modules.newModule')}
          description={t('tenant.modules.modal.description')}
        >
          <div className="space-y-4">
            <FormField label={t('tenant.modules.fields.name')} required>
              <Input
                value={newModule.name}
                onChange={(e) => setNewModule({ ...newModule, name: e.target.value })}
                placeholder={t('tenant.modules.fields.namePlaceholder')}
                autoFocus
                data-testid="module-name-input"
                maxLength={100}
              />
            </FormField>

            <FormField label={t('tenant.modules.fields.description')}>
              <Textarea
                value={newModule.description}
                onChange={(e) => setNewModule({ ...newModule, description: e.target.value })}
                rows={2}
                maxLength={500}
                data-testid="module-description-input"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField label={t('tenant.modules.fields.icon')}>
                <IconPicker
                  value={newModule.icon}
                  onChange={(icon) => setNewModule({ ...newModule, icon })}
                  accentColor={newModule.color}
                />
              </FormField>

              <FormField label={t('tenant.modules.fields.color')}>
                <div className="flex flex-wrap gap-2" data-testid="module-color-picker">
                  {PRESET_COLORS.map((color) => {
                    const selected = newModule.color === color;
                    return (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewModule({ ...newModule, color })}
                        aria-label={color}
                        aria-pressed={selected}
                        className={
                          'h-8 w-8 rounded-full transition ' +
                          (selected ? 'ring-2 ring-offset-2 ring-text-primary' : 'hover:scale-110')
                        }
                        style={{ backgroundColor: color }}
                      />
                    );
                  })}
                </div>
              </FormField>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newModule.showInMenu}
                onChange={(e) => setNewModule({ ...newModule, showInMenu: e.target.checked })}
                data-testid="module-show-in-menu"
                className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
              />
              <span className="text-text-secondary">{t('tenant.modules.fields.showInMenu')}</span>
            </label>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setNewModule(DEFAULT_NEW_MODULE);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              loading={createMutation.isPending}
              disabled={!newModule.name.trim() || createMutation.isPending}
              data-testid="module-create-submit"
            >
              {t('tenant.modules.createAndOpenBuilder')}
            </Button>
          </div>
        </Modal>

        <ConfirmDialog
          open={pendingDelete !== null}
          title={t('common.delete')}
          message={t('tenant.modules.confirmDelete', { name: pendingDelete?.name ?? '' })}
          confirmLabel={t('common.delete')}
          onConfirm={() => {
            if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
            setPendingDelete(null);
          }}
          onClose={() => setPendingDelete(null)}
        />
      </div>
    </main>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────

function EmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <Card padding="lg" className="text-center" data-testid="modules-empty">
      <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-bg-tertiary">
        <Database className="h-6 w-6 text-text-muted" aria-hidden="true" />
      </div>
      <p className="mt-3 font-semibold text-text-primary">{t('tenant.modules.empty.title')}</p>
      <p className="mt-1 text-sm text-text-tertiary">{t('tenant.modules.empty.subtitle')}</p>
      <Button
        variant="primary"
        className="mt-4"
        onClick={onCreate}
        leftIcon={<Plus className="h-4 w-4" />}
      >
        {t('tenant.modules.newModule')}
      </Button>
    </Card>
  );
}

// ─── Module card ────────────────────────────────────────────────────────────

interface ModuleCardProps {
  mod: ModuleListItem;
  onDelete: () => void;
  onRestore: () => void;
  restoreLoading?: boolean;
}

function ModuleCard({ mod, onDelete, onRestore, restoreLoading }: ModuleCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const isDeleted = !!mod.gcrecord;

  return (
    <>
      <Card
        padding="md"
        className={
          'relative transition ' +
          (isDeleted
            ? 'border-warning-300 bg-warning-50/40 opacity-75'
            : 'cursor-pointer hover:border-accent-500 hover:shadow-md')
        }
        onClick={isDeleted ? undefined : () => navigate(`/modules/${mod.id}/settings`)}
        data-testid={`module-card-${mod.slug}`}
      >
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${mod.color}20`, color: mod.color }}
            aria-hidden="true"
          >
            {renderIconByName(mod.icon, 'h-5 w-5')}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div
                className="block text-left font-semibold text-text-primary"
                data-testid={`module-card-${mod.slug}-records-link`}
              >
                {mod.name}
              </div>
              {isDeleted && (
                <span className="rounded-full bg-warning-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning-700">
                  {t('tenant.modules.deletedBadge')}
                </span>
              )}
            </div>
            {mod.description && (
              <p className="mt-1 text-xs text-text-tertiary line-clamp-2">{mod.description}</p>
            )}
            <p className="mt-2 text-xs text-text-muted">/{mod.slug}</p>
          </div>
          {isDeleted ? (
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRestore();
              }}
              loading={restoreLoading}
              disabled={restoreLoading}
              data-testid={`module-card-${mod.slug}-restore-btn`}
            >
              {t('tenant.modules.actions.restore')}
            </Button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
              aria-label={t('common.moreActions')}
              aria-expanded={menuOpen}
              className="rounded-md p-1 text-text-muted hover:bg-bg-tertiary"
              data-testid={`module-card-${mod.slug}-menu-btn`}
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          )}
        </div>

        {menuOpen && (
          <div
            className="absolute right-3 top-12 z-10 min-w-[180px] rounded-md border border-border-primary bg-bg-primary py-1 shadow-lg"
            role="menu"
            data-testid={`module-card-${mod.slug}-menu`}
          >
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                navigate(`/modules/${mod.id}/settings`);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary hover:bg-bg-tertiary"
              data-testid={`module-card-${mod.slug}-menu-settings`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {t('tenant.modules.actions.openSettings')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                setEditOpen(true);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary hover:bg-bg-tertiary"
              data-testid={`module-card-${mod.slug}-menu-edit-module`}
            >
              <Pencil className="h-4 w-4" />
              {t('tenant.modules.actions.quickEdit')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                navigate(`/modules/${mod.id}/settings?tab=layout`);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary hover:bg-bg-tertiary"
              data-testid={`module-card-${mod.slug}-menu-edit-builder`}
            >
              <SettingsIcon className="h-4 w-4" />
              {t('tenant.modules.actions.editBuilder')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                onDelete();
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4" />
              {t('tenant.modules.actions.delete')}
            </button>
          </div>
        )}
      </Card>

      {/* EditModuleModal POZA Card — React bubbluje eventy przez drzewo komponentów,
          nie DOM. Gdyby był wewnątrz Card, klik w modal trafiałby do onClick karty. */}
      <EditModuleModal moduleId={mod.id} open={editOpen} onClose={() => setEditOpen(false)} />
    </>
  );
}
